import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface ApiResponse<T> {
  status: number;
  success: boolean;
  message: string;
  data: T;
}

export interface PaginatedResponse<T> {
  status: number;
  success: boolean;
  message: string;
  data: {
    list: T[];
    pagination: { total: number; page: number; limit: number; totalPages: number };
  };
}


export interface MenuCategory {
  public_id: string;
  name: string;
  slug: string;
  image_url: string | null;
  subcategory: { public_id: string; name: string }[];
}

export interface Subcategory {
  id: string;
  name: string;
  tags: {
    id: string;
    name: string;
  }[];
}


export interface ProductCard {
  id: string;
  publicId: string;
  name: string;
  slug: string;
  basePrice: number;
  discountPercentage: number;
  finalPrice: number;
  isFeatured: boolean;
  hasDiscount: boolean;
  images: { url: string; isPrimary: boolean }[];
  attributes: { name: string; code: string; value: string; displayLocationId: string | null }[];
}

export interface ProductDetail {
  id: string;
  publicId: string;
  name: string;
  slug: string;
  shortDescription: string | null;
  longDescription: string | null;
  basePrice: number;
  discountPercentage: number;
  finalPrice: number;
  hasDiscount: boolean;
  isFeatured: boolean;
  confectionType: number;
  shippingType: number;
  category: { public_id: string; name: string; slug: string };
  images: { id: string; url: string; isPrimary: boolean }[];
  attributes: {
    id: string;
    name: string;
    code: string;
    inputType: string;
    displayLocationId: string | null;
    selectedValues: string[];
    selectedOptions: { id: string | number; value: string; displayLabel: string | null }[];
    options: { id: string | number; value: string; display_label: string }[];
  }[];
  informationItems: { id: string; content: string; sort_order: number }[];
  reviews: { id: string; rating: number; comment: string; customerName: string; createdAt: string }[];
  variants: { id: string; publicId: string; sku: string; variant_name: string | null; price: number; discount_percentage: number; final_price: number | null }[];
}


export interface Banner {
  id: string;
  name: string;
  image_url: string;
  sort_order: number;
}


export interface SearchProductsRequest {
  page?: number;
  pageSize?: number;
  name?: string;
  categoryId?: string;
  confectionType?: number;
  categoryName?: string;
  subCategories?: { subCategoryId?: string; tagId?: string; subTagsIds?: string[] }[];
}

@Injectable({ providedIn: 'root' })
export class ApiService {

  private readonly baseUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}


  getMenu(): Observable<ApiResponse<MenuCategory[]>> {
    return this.http.get<ApiResponse<MenuCategory[]>>(`${this.baseUrl}/menu`);
  }

  getCategories(): Observable<ApiResponse<MenuCategory[]>> {
    return this.http.get<ApiResponse<MenuCategory[]>>(`${this.baseUrl}/categories`);
  }

  getSubcategories(categoryId: string): Observable<ApiResponse<Subcategory[]>> {
    return this.http.get<ApiResponse<Subcategory[]>>(`${this.baseUrl}/categories/${categoryId}/subcategories`);
  }


  getTags(): Observable<ApiResponse<any[]>> {
    return this.http.get<ApiResponse<any[]>>(`${this.baseUrl}/tags`);
  }

  getSubtags(tagId: string, search?: string, page = 1, limit = 20): Observable<ApiResponse<any[]> & { pagination?: any }> {
    let params = `?page=${page}&limit=${limit}`;
    if (search) params += `&search=${encodeURIComponent(search)}`;
    return this.http.get<ApiResponse<any[]> & { pagination?: any }>(`${this.baseUrl}/tags/${tagId}/subtags${params}`);
  }


  getBanners(): Observable<ApiResponse<Banner[]>> {
    return this.http.get<ApiResponse<Banner[]>>(`${this.baseUrl}/banners`);
  }


  searchProducts(body: SearchProductsRequest): Observable<PaginatedResponse<ProductCard>> {
    return this.http.post<PaginatedResponse<ProductCard>>(`${this.baseUrl}/products/search`, body);
  }

  getProductDetail(publicId: string): Observable<ApiResponse<ProductDetail>> {
    return this.http.get<ApiResponse<ProductDetail>>(`${this.baseUrl}/products/${publicId}`);
  }

  getProductCustomization(publicId: string): Observable<ApiResponse<any[]>> {
    return this.http.get<ApiResponse<any[]>>(`${this.baseUrl}/products/${publicId}/customization`);
  }
}
